import * as React from 'react'
import styled from 'theme/styled-components'
import Theme from 'theme/Theme'

import useI18n from 'i18n/useI18n'
import { windowSizeBreakpoints } from 'utils/breakpoints'

import Icons from 'components/icons/Icons'
import Modal from 'components/modal/Modal'

interface Props {
  setSelectedEntry: (entry: SiteLocationDetailsV6) => void
  entries: SiteLocationDetailsV6[]
  goBack?: () => void
}

const EntryModal = ({ setSelectedEntry, entries, goBack }: Props) => {
  const i18n = useI18n()

  const selectEntry = (entry: SiteLocationDetailsV6) => {
    setSelectedEntry(entry)
    Modal.close()
  }

  const renderBuilding = (entry: SiteLocationDetailsV6, index: number) => {
    const isLast = entries.length === index + 1

    return (
      <EntryContainer>
        <EntryText onClick={() => selectEntry(entry)}>{i18n.t(`common.${entry.name}`)}</EntryText>
        {!isLast && <BlueLineBreak role="presentation" />}
      </EntryContainer>
    )
  }

  return (
    <MainContainer>
      <TitleContainer>
        {!!goBack && (
          <CrossContainer
            onClick={() => {
              goBack()
              Modal.close()
            }}>
            <Icons name="chevron_left" size={25} color={Theme.colors.blue} />
          </CrossContainer>
        )}

        <Title>{i18n.t('screens.incident.form.whichEntry')}</Title>

        <CrossContainer onClick={() => Modal.close()} aria-label={i18n.t('label.ariaLabel.windowCrossIcon')}>
          <Icons name="cross" size={25} color={Theme.colors.blue} />
        </CrossContainer>
      </TitleContainer>

      <ListContainer>
        <CategoryListContainer>{entries.map(renderBuilding)}</CategoryListContainer>
      </ListContainer>
    </MainContainer>
  )
}

// CONTAINERS

const MainContainer = styled('div')`
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  width: 444px;

  @media only screen and (max-width: ${windowSizeBreakpoints.phone}px) {
    width: 90vw;
  }
`

const TitleContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 30px 40px 20px;
  @media only screen and (max-width: ${windowSizeBreakpoints.phone}px) {
    padding: 20px 30px 10px;
  }
`

const CrossContainer = styled('button')`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
`

const ListContainer = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0px 40px 40px;
  overflow-y: scroll;

  /* Style scrollbar pour Chrome, Safari and Opera */
  ::-webkit-scrollbar {
    width: 5px;
    background: white;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 5px;
    background: ${(props) => props.theme.colors.scrollBar};
  }

  /* Style scrollbar pour Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${(props) => props.theme.colors.scrollBar} transparent;
  @media only screen and (max-width: ${windowSizeBreakpoints.phone}px) {
    padding: 10px 30px;
  }
`

const CategoryListContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding-left: 0px;
`

const EntryContainer = styled('li')`
  flex: 1;
  justify-content: center;
  list-style: none;
`

// TEXTES

const Title = styled('h1')`
  display: flex;
  flex: 1;
  margin: 0px;
  ${(props) => props.theme.fonts.title};
  overflow-wrap: anywhere;
`

const EntryText = styled('h2')`
  cursor: pointer;
  ${(props) => props.theme.fonts.bodyBold};
  color: ${(props) => props.theme.colors.darkGrey};
  margin: 0px;
`

// AUTRES

const BlueLineBreak = styled('div')`
  width: 60px;
  height: 3px;
  background-color: ${(props) => props.theme.colors.blue};
  border-radius: 2px;
  margin: 15px 0px;
`

export default EntryModal
